package com.example.mystybag.sharedmodels.project.responseModels.merchant

import kotlinx.serialization.Serializable

/** A product category (folder) inside a location's catalog. */
@Serializable
data class ProductCategoryModel(
    val id: Long,
    /** null = top-level (root) category. */
    val parentCategoryId: Long? = null,
    val name: String,
    val sortOrder: Int = 0
)

/** A single product in a location's catalog. */
@Serializable
data class ProductModel(
    val id: Long,
    /** null = product is at the location root (not inside any category). */
    val categoryId: Long? = null,
    val name: String,
    val description: String = "",
    /** Approximate weight in grams shown to consumers (0 = unspecified). */
    val grams: Int = 0,
    val photoUrl: String = "",
    val isEnabled: Boolean = true,
    val sortOrder: Int = 0,
    /** Allergen ids (nomenclature_allergens) attached to this product. */
    val allergenIds: List<Long> = emptyList()
)

/** Lightweight product entry used when creating/updating a location's catalog. */
@Serializable
data class LocationProductInput(
    val name: String,
    val grams: Int = 0
)

/** Builds the consumer-facing "name, grams" display label, e.g. "Bread 200g". */
fun ProductModel.displayLabel(): String =
    if (grams > 0) "$name, ${grams}g" else name

/**
 * A set of products that belong to the same catalog category, used to render
 * the consumer "possible products" view grouped by category.
 */
data class ProductGroup(
    /** null when the products are at the location root (not inside any category). */
    val categoryId: Long?,
    /** Localized category name; blank/null when the group has no resolvable category. */
    val name: String?,
    val products: List<ProductModel>
)

/** Number of distinct catalog categories these products span (null/root counts as one). */
fun List<ProductModel>.distinctCategoryCount(): Int =
    mapTo(mutableSetOf()) { it.categoryId }.size

/**
 * Groups products by their [ProductModel.categoryId], preserving the order in which each
 * category first appears. [categories] supplies the human-readable group names.
 */
fun List<ProductModel>.groupByCategory(
    categories: List<ProductCategoryModel> = emptyList()
): List<ProductGroup> {
    val nameById = categories.associate { it.id to it.name }
    val buckets = LinkedHashMap<Long?, MutableList<ProductModel>>()
    for (product in this) {
        buckets.getOrPut(product.categoryId) { mutableListOf() }.add(product)
    }
    return buckets.entries.map { (categoryId, items) ->
        ProductGroup(
            categoryId = categoryId,
            name = categoryId?.let { nameById[it] },
            products = items
        )
    }
}

/** Serializes products back into the legacy ";"-separated form (for edit-form prefill in the apps). */
fun List<ProductModel>.toLegacyWhatYouCouldGetString(): String =
    filter { it.name.isNotBlank() }
        .joinToString(";") { p -> if (p.grams > 0) "${p.name},${p.grams}" else p.name }

/** Parses the legacy ";"-separated "name,grams" form into structured product inputs. */
fun parseLegacyProductsString(raw: String?): List<LocationProductInput> {
    if (raw.isNullOrBlank()) return emptyList()
    return raw.split(';')
        .mapNotNull { token ->
            val cleaned = token.trim()
            if (cleaned.isEmpty()) return@mapNotNull null
            val parts = cleaned.split(',', limit = 2)
            val name = parts.getOrNull(0)?.trim().orEmpty()
            if (name.isEmpty()) return@mapNotNull null
            val grams = parts.getOrNull(1)?.trim().orEmpty().filter { it.isDigit() }.toIntOrNull() ?: 0
            LocationProductInput(name = name, grams = grams)
        }
}

/**
 * How a bag's products are chosen.
 *  - ALL:      every product of the location at the time of creation
 *  - CATEGORY: every product in [categoryIds] and their sub-categories
 *  - CUSTOM:   the explicit set of [productIds]
 */
@Serializable
enum class BagProductSelectionMode {
    ALL,
    CATEGORY,
    CUSTOM;

    /** Wire value stored in the database / sent to the server. */
    val wireValue: String
        get() = when (this) {
            ALL -> "all"
            CATEGORY -> "category"
            CUSTOM -> "custom"
        }

    companion object {
        fun fromWire(value: String?): BagProductSelectionMode = when (value) {
            "category" -> CATEGORY
            "custom" -> CUSTOM
            else -> ALL
        }
    }
}

/** Product selection sent when creating/updating a bag. */
@Serializable
data class BagProductSelection(
    val mode: BagProductSelectionMode = BagProductSelectionMode.ALL,
    /**
     * Kept for backward compatibility with servers that accept a single category.
     * Populated with the only selected category when exactly one is chosen.
     */
    val categoryId: Long? = null,
    /** Selected categories when [mode] == CATEGORY (supports multiple). */
    val categoryIds: List<Long> = emptyList(),
    /** Used when [mode] == CUSTOM. */
    val productIds: List<Long> = emptyList()
)
